/**
 * Tutorial versioning.
 *
 * Versions are semver-style `major.minor.patch`. Older tutorials stored a bare
 * integer `N`; we treat that as `N.0.0`. The editor bumps the patch on every save,
 * and user completion is keyed on the *major* component only — so routine patch/minor
 * edits never re-show a tutorial that a user already completed, while a deliberate
 * major bump does.
 */
export type SemVer = { major: number; minor: number; patch: number };

export const parseVersion = (version: number | string | undefined): SemVer => {
  if (typeof version === "number") {
    return { major: Math.max(1, Math.floor(version)), minor: 0, patch: 0 };
  }
  const parts = String(version ?? "1")
    .trim()
    .split(".")
    .map((p) => Number.parseInt(p, 10));
  const major = parts[0];
  const minor = parts[1];
  const patch = parts[2];
  return {
    major: Number.isFinite(major) && major > 0 ? major : 1,
    minor: Number.isFinite(minor) && minor >= 0 ? minor : 0,
    patch: Number.isFinite(patch) && patch >= 0 ? patch : 0,
  };
};

export const formatVersion = (v: SemVer): string =>
  `${v.major}.${v.minor}.${v.patch}`;

/** Returns the next patch version as a string, e.g. `1.0.0` -> `1.0.1`, `2` -> `2.0.1`. */
export const bumpPatch = (version: number | string | undefined): string => {
  const v = parseVersion(version);
  return formatVersion({ major: v.major, minor: v.minor, patch: v.patch + 1 });
};

/** The major component, used to key user completion state. */
export const majorVersion = (version: number | string | undefined): number =>
  parseVersion(version).major;
